import {
  IonBadge,
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonLabel,
  IonList,
  IonLoading,
  IonModal,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { checkmark, checkmarkCircle, close } from 'ionicons/icons';
import { orderBy } from 'lodash';
import { ReactElement, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInitiateAuth } from '../../api/auth.hooks';
import {
  TenantAssignment,
  useListTenantAssignmentsQuery,
} from '../../api/graphql/generated';
import { useCurrentUser } from '../../api/user.hooks';
import { useBeaconRouter } from '../../route/route.hooks';
import { useNavToLastKnownLocation } from '../../store/nav-history/nav-history.hooks';
import {
  useActiveWorkspace,
  useIsAuthenticated,
  useIsAuthenticatedLazy,
} from '../../store/workspace/workspace.hooks';
import { logger } from '../../util/logger';
import { VERSION } from '../../version';
import { LoadingRetry } from '../graphql/LoadingRetry';
import { IonItem } from '../ionic/IonItem';
import { DateField } from '../ui/date-field';
import './workspaces.css';

interface WorkspaceSwitcherProps {
  onSelect: (tenantAssignment: TenantAssignment) => void;
}

const WorkspaceSwitcher = (props: WorkspaceSwitcherProps): ReactElement => {
  const { onSelect } = props;

  const { data, loading, error, refetch } = useListTenantAssignmentsQuery({
    fetchPolicy: 'no-cache',
  });

  const workspaces = orderBy(
    data?.listTenantAssignments ?? [],
    [(w) => w.user.activityAt, (w) => w.tenant.name],
    ['desc', 'asc']
  );

  return (
    <LoadingRetry
      loading={loading}
      error={error}
      refetch={refetch}
      results={{
        hasResults: workspaces.length > 0,
        noResultsComponent: null,
      }}
    >
      <IonList lines="full">
        {workspaces.map((tenantAssignment) => {
          return (
            <TenantItem
              key={tenantAssignment.tenant.id}
              tenantAssignment={tenantAssignment}
              onSelect={onSelect}
            />
          );
        })}
      </IonList>
    </LoadingRetry>
  );
};

interface TenantItemProps {
  tenantAssignment: TenantAssignment;
  onSelect: (tenantAssignment: TenantAssignment) => void;
}

const TenantItem = (props: TenantItemProps): ReactElement => {
  const { tenantAssignment, onSelect } = props;
  const tenantId = tenantAssignment.tenant.id;
  const { i18n } = useTranslation();

  const activeWorkspace = useActiveWorkspace();
  const isActiveTenant = activeWorkspace?.tenant.id === tenantId;

  const unread = tenantAssignment?.user.unreadNotifications ?? 0;
  const unreadBadge =
    unread > 0 ? <IonBadge slot="end">{unread}</IonBadge> : null;

  const isAuthenticated = useIsAuthenticated(tenantId);

  return (
    <IonItem
      href="#"
      onClick={(e) => {
        e.preventDefault();
        onSelect(tenantAssignment);
      }}
    >
      <IonLabel>
        <h2>
          {tenantAssignment?.tenant?.name || 'No name'}
          {isAuthenticated && (
            <IonIcon color="success" icon={checkmarkCircle} />
          )}
        </h2>
        <p>
          {tenantAssignment?.user?.activityAt ? (
            <DateField date={tenantAssignment?.user?.activityAt} />
          ) : (
            i18n.t('common:last_activity_never')
          )}
        </p>
      </IonLabel>
      {isActiveTenant ? (
        <IonIcon slot="end" color={'primary'} icon={checkmark} />
      ) : (
        unreadBadge
      )}
    </IonItem>
  );
};

interface WorkspaceSwitcherModalRenderProps {
  showWorkspaceSwitcher: () => void;
}

interface WorkspaceSwitcherModalProps {
  children: (renderProps: WorkspaceSwitcherModalRenderProps) => ReactElement;
}

const WorkspaceSwitcherModal = (
  props: WorkspaceSwitcherModalProps
): ReactElement => {
  const { children } = props;
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [showLoading, setShowLoading] = useState<boolean>(false);
  const checkAuthenticated = useIsAuthenticatedLazy();
  const user = useCurrentUser();
  const showWorkspaceSwitcher = useCallback(() => {
    setIsOpen(true);
  }, []);

  const modalDismissCbRef = useRef<() => void>();

  const [initiateAuth] = useInitiateAuth();
  const { navToLastKnownTenantLocation } = useNavToLastKnownLocation();
  const activeWorkspace = useActiveWorkspace();

  const { navigateToLogout } = useBeaconRouter();

  const onSwitchTenant = useCallback(
    (tenantId: string) => {
      const isAuthenticated = checkAuthenticated(tenantId);
      const isActiveTenant = activeWorkspace?.tenant.id === tenantId;
      if (!isActiveTenant && user?.email) {
        if (isAuthenticated) {
          logger.info('switching to authenticated tenant', { tenantId });
          navToLastKnownTenantLocation(tenantId);
        } else {
          logger.info('switching to unauthenticated tenant', { tenantId });
          initiateAuth({
            variables: {
              input: { email: user?.email, tenantId },
            },
          });
        }
      }
    },
    [
      activeWorkspace?.tenant.id,
      checkAuthenticated,
      initiateAuth,
      navToLastKnownTenantLocation,
      user?.email,
    ]
  );

  return (
    <>
      {children({ showWorkspaceSwitcher })}
      <IonModal
        isOpen={isOpen}
        onDidDismiss={() => {
          setIsOpen(false);
          if (modalDismissCbRef.current) {
            modalDismissCbRef.current();
            modalDismissCbRef.current = undefined;
          }
        }}
      >
        <IonHeader>
          <IonToolbar>
            <IonButtons slot={'start'}>
              <IonButton onClick={() => setIsOpen(false)}>
                <IonIcon icon={close} size="large" />
              </IonButton>
            </IonButtons>
            <IonButtons slot={'end'}>
              <IonButton
                href={'#'}
                onClick={(e) => {
                  e.preventDefault();
                  modalDismissCbRef.current = () => {
                    navigateToLogout();
                  };
                  setIsOpen(false);
                }}
              >
                <IonLabel>{i18n.t('common:logout')}</IonLabel>
              </IonButton>
            </IonButtons>
            <IonTitle>{i18n.t('common:workspaces')}</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <WorkspaceSwitcher
            onSelect={(tenantAssignment) => {
              modalDismissCbRef.current = () => {
                onSwitchTenant(tenantAssignment.tenant.id);
              };
              setIsOpen(false);
            }}
          />
        </IonContent>
        <IonFooter>
          <IonToolbar className="ion-text-center">
            <IonLabel>
              <p>
                {i18n.t('common:app_version')}: {VERSION}
              </p>
            </IonLabel>
          </IonToolbar>
        </IonFooter>
      </IonModal>
      <IonLoading
        isOpen={showLoading}
        onDidDismiss={() => setShowLoading(false)}
        message={i18n.t('common:please_wait')}
      />
    </>
  );
};

export { WorkspaceSwitcher, WorkspaceSwitcherModal };
